import React, { useContext, useEffect, useState } from "react";
import { Layout } from "@shopify/polaris";
import { useQuery, useLazyQuery } from "@apollo/client";

// import hoc
import { withFeature, withErrorBoundary } from "lib/hoc";

// import constant
import constant from "lib/constant/constant";

// import context
import { PrivateContext } from "lib/context/privateContext";

// import helper
import { baseHelper } from "lib/helpers";

// import queries
import {
  GET_ADVANCE_METAFIELD,
  GET_AVAILABILITY,
  GET_METAFIELD_SETTING,
  GET_PRODUCT,
} from "app/product/apollo/queries";
import { GET_GEOLOCATION_SETTING } from "app/setup/apollo";

// import components
import Banner from "lib/components/banner/banner";
import { SkeletonAnnotated } from "lib/components";

// import tab
import { useParams } from "react-router-dom";
import EditTabs from "./features/form/tab";
import EditServiceTabs from "./features/form/service/serviceTabs";

const ProductEdit = () => {
  const { isLoading } = useContext(PrivateContext);
  const { id } = useParams();
  const [description, setDescription] = useState("");
  const [title, setTitle] = useState("");
  const [locations, setLocation] = useState([]);
  const [isVariant, setIsVariant] = useState(false);
  const [isMetafield, setIsMetafield] = useState(false);
  const [isCampaign, setIsCampaign] = useState(false);
  const [isService, setIsService] = useState(false);
  const [availability, setAvailability] = useState({});
  const [serviceId, setServiceId] = useState("");
  const [catalogIds, setCatalogIds] = useState([]);

  const [banner, setBanner] = useState({
    action: null,
    children: null,
    isOpen: false,
    status: "",
    title: "",
  });
  const onDismissBanner = () => {
    setBanner({
      action: null,
      children: null,
      isOpen: false,
      status: "",
      title: "",
    });
  };

  const { loading: productLoading, data: productData, refetch } = useQuery(GET_PRODUCT, {
    variables: { input: { id } },
  });

  const { loading: locationLoading, data: locationData } = useQuery(GET_GEOLOCATION_SETTING);

  const { loading: metafieldLoading, data: metaFieldData } = useQuery(GET_METAFIELD_SETTING);
  const { data: advanceData, loading: advanceLoading } = useQuery(GET_ADVANCE_METAFIELD);

  const [getAvailability, { loading: availabilityLoading, data: availabilityData }] = useLazyQuery(GET_AVAILABILITY);

  useEffect(() => {
    if (metaFieldData) {
      const metafieldSetting = baseHelper.getResponseData(metaFieldData, constant.gql.GET_METAFIELD_SETTING) || {};
      const advanceSetting = baseHelper.getResponseData(advanceData, "getAdvanceMetafieldSetting") || {};
      const { metafield = [] } = metafieldSetting;
      const { advanceMetafield } = advanceSetting || {};
      const { isEnabled, isAll, customSetting } = advanceMetafield || {};

      if (metafield && metafield.length) {
        if (isEnabled) {
          let isAllow = false;
          if (isAll) {
            isAllow = true;
          } else {
            const advanceFilterData = (customSetting || []).filter((item) => catalogIds.includes(item.catalogId));
            if (advanceFilterData && advanceFilterData.length) {
              isAllow = true;
            }
          }
          setIsMetafield(isAllow);
          return;
        }
        setIsMetafield(true);
        return;
      }
      setIsMetafield(false);
    }
  }, [advanceData, catalogIds, metaFieldData]);

  useEffect(() => {
    if (locationData) {
      const locationSetting = baseHelper.getResponseData(locationData, constant.gql.GET_GEOLOCATION_SETTING) || {};
      const { isAllowed = false, locations: locationArray = [] } = locationSetting;
      if (isAllowed && locationArray && locationArray.length) {
        setLocation(locationArray);
      }
    }
  }, [locationData]);

  useEffect(() => {
    if (productData) {
      const productResponse = baseHelper.getResponseData(productData, constant.gql.GET_PRODUCT) || {};
      const {
        variants = [],
        serviceId: productServiceId,
        isService: isProductService,
        shopifyCatalogId = [],
        listingQuantity,
      } = productResponse || {};
      setCatalogIds(shopifyCatalogId || []);
      if (productServiceId) {
        setServiceId(productServiceId);
        getAvailability({ variables: { input: { id: productServiceId } } });
      }

      setIsService(isProductService);
      setIsCampaign(!!listingQuantity);
      setIsVariant(!!(variants && variants.length));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productData]);

  useEffect(() => {
    if (availabilityData) {
      const productResponse = baseHelper.getResponseData(availabilityData, constant.gql.GET_SERVICE) || {};
      if (productResponse) {
        setAvailability(productResponse);
      }
    }
  }, [availabilityData]);

  if (isLoading || productLoading || locationLoading || metafieldLoading || availabilityLoading || advanceLoading) {
    return <SkeletonAnnotated />;
  }
  const RenderTabComponent = isService ? EditServiceTabs : EditTabs;

  return (
    <>
      {banner.isOpen && (
        <Layout.Section>
          <Banner
            title={banner.title}
            status={banner.status}
            isOpen={banner.isOpen}
            action={banner.action}
            onDismiss={onDismissBanner}
          >
            {banner.children}
          </Banner>
        </Layout.Section>
      )}
      <br />
      <Layout.AnnotatedSection title={title} description={description}>
        <RenderTabComponent
          setBanner={setBanner}
          setDescription={setDescription}
          setTitle={setTitle}
          isVariant={isVariant}
          refetch={refetch}
          setIsVariant={setIsVariant}
          isMetafield={isMetafield}
          availability={availability}
          serviceId={serviceId}
          getAvailability={getAvailability}
          locations={locations}
          isCampaign={isCampaign}
        />
      </Layout.AnnotatedSection>
    </>
  );
};

export default withFeature(withErrorBoundary(ProductEdit), { feature: constant.PRODUCT_UPDATE });
